import { ref, readonly } from "vue";
import type { ApiVersionResponse } from "../../types";
import { useRuntimeConfig } from "#imports";

export const useVersionCheck = () => {
  const intervalId = ref<ReturnType<typeof setInterval> | null>(null);
  const isChecking = ref(false);
  const {
    public: { versionCheck },
  } = useRuntimeConfig();

  const checkVersion = async (): Promise<void> => {
    if (!versionCheck.enabled || isChecking.value) return;

    try {
      isChecking.value = true;

      // Fetch with cache-busting query parameter
      const response = (await fetch(
        `${versionCheck.endpoint}?t=${Date.now()}`,
        {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        },
      ).then((res) => res.json())) as ApiVersionResponse;

      const currentVersion = versionCheck.buildName;
      const newVersion = response.version;

      if (currentVersion !== newVersion) {
        window.location.reload();
      }
    } catch (error) {
      window.console.error("[Version Check] Failed to check version:", error);
    } finally {
      isChecking.value = false;
    }
  };

  const startVersionCheck = (): void => {
    if (!versionCheck.enabled) {
      return;
    }

    // Stop any existing interval
    stopVersionCheck();

    // Perform initial check after a short delay
    setTimeout(() => {
      checkVersion();
    }, 5000);

    // Set up interval for periodic checks
    const intervalMs = versionCheck.checkInterval * 1000;
    intervalId.value = setInterval(() => {
      checkVersion();
    }, intervalMs);
  };

  const stopVersionCheck = (): void => {
    if (!intervalId.value) return;
    clearInterval(intervalId.value);
    intervalId.value = null;
  };

  // Clean up on unmount
  if (import.meta.client) {
    window.addEventListener("beforeunload", () => {
      stopVersionCheck();
    });
  }

  return {
    checkVersion,
    startVersionCheck,
    stopVersionCheck,
    isChecking: readonly(isChecking),
  };
};
